import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxTooltipDirectivesModule } from 'ngx-tooltip-directives';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
// services
import { MessageService } from './services/message.service';
import { SharedService } from './services/shared.service';
import { UserService } from './services/user.service';
import { ProductionPlanService } from './services/production-plan.service';
import { RequestService } from '../request/request.service';

import { DatePickerModule } from "primeng/datepicker";
// pipes
import { SearchPipe } from './pipes/search.pipe';
import { TimeAgoPipe } from './pipes/time-ago.pipe';
import { KeysPipe } from './pipes/object-keys.pipe';
import { DigitFormatPipe } from './pipes/digit-format.pipe';
// helpers
import { DateHelper } from './helpers/date.helper';
import { RoleHelper } from './helpers/role.helper';
import { SectorHelper } from './helpers/sector.helper';
import { PermissionHelper } from './helpers/permission.helper';
import { ShiftHelper } from './helpers/shift.helper';
import { ConstantsHelper } from './helpers/constants.helper';
// directives
import { ClickElsewhereDirective } from './directives/click-elsewhere.directive';
import { DisableControlDirective } from './directives/disable-control.directive';
import { ShowPasswordDirective } from './directives/show-password.directive';
import { StopPropagationDirective } from './directives/stop-propagation.directive';
import { BackButtonDirective } from './directives/back-button.directive';
import { DisableButtonDuringRequestDirective } from './directives/disable-button-during-request.directive';
// dropdown components
import { DropDownComponent } from './components/dropdown/dropdown.component';
import { OperationDropdownComponent } from './components/dropdown/operation/operation-dropdown.component';
import { MaterialDropdownComponent } from './components/dropdown/material/material-dropdown.component';
import { OrderDropdownComponent } from './components/dropdown/order/order-dropdown.component';
import { MachineDropdownComponent } from './components/dropdown/machine/machine-dropdown.component';
import { ObjectDropdownComponent } from './components/dropdown/object/object-dropdown.component';
// other shared components
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { HeaderComponent } from './components/header/header.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { QuestionModalComponent } from './components/modal/question/question-modal.component';
import { SearchComponent } from './components/search/search.component';
import { SmallScreenComponent } from './components/small-screen/small-screen.component';
import { UploadComponent } from './components/upload/upload.component';
import { EmailComponent } from './components/email/email.component';
import { MaterialComponent } from './components/material/material.component';
import { TagComponent } from './components/tag/tag.component';
import { OperationComponent } from './components/operation/operation.component';
import { ProductionPlanHeaderComponent } from './components/production-plan/header/production-plan-header.component';
import { OperationItemComponent } from './components/production-plan/operation-item/operation-item.component';
import { OrderItemComponent } from './components/production-plan/order-item/order-item.component';
import { EmployeeAvailabilityComponent} from './components/production-plan/availability/employee/employee-availability.component';
import { DeleteComponent } from './components/delete/delete.component';
import { SectorTagComponent } from './components/sector-tag/sector-tag.component';
import { MachineAvailabilityComponent} from './components/production-plan/availability/machine-availability/machine-availability.component';
import { ProductSegmentComponent } from './components/product-segment/product-segment.component';
import { SortComponent } from './components/sort/sort.component';
import { CalendarComponent } from "./components/calendar/calendar.component";
import { ActivityLogComponent } from './components/activity-log/activity-log.component';
// progress bar components
import { ProgressCircleComponent } from './components/progress/circle/progress-circle.component';
import { ProgressBarComponent } from './components/progress/bar/progress-bar.component';
import { ProgressPercentComponent } from './components/progress/percent/progress-percent.component';
// autocomplete components
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { ClientAutocompleteComponent } from './components/autocomplete/client/client-autocomplete.component';
import { MaterialAutocompleteComponent } from './components/autocomplete/material/material-autocomplete.component';
import { OrderAutocompleteComponent } from './components/autocomplete/order/order-autocomplete.component';
import { ProductionOrderAutocompleteComponent } from './components/autocomplete/production-order/production-order-autocomplete.component';
import { ActiveOperationsAutocompleteComponent } from './components/autocomplete/active-operations/active-operations-autocomplete.component';
import { RequestAutocompleteComponent } from './components/autocomplete/request/request-autocomplete.component';
import { ProductAutocompleteComponent } from './components/autocomplete/product/product-autocomplete.component';
import { OperationAutocompleteComponent } from './components/autocomplete/operation/operation-autocomplete.component';
import { ClientIncomeEstimationAutocompleteComponent } from './components/autocomplete/client-income-estimation/client-income-estimation-autocomplete.component';
import { TransporterAutocompleteComponent } from './components/autocomplete/transporter/transporter-autocomplete.component';
// modal components
import { AlternativeMaterialComponent } from './components/modal/alternative-material/alternative-material.component';
import { CommentModalComponent } from './components/modal/comment/comment-modal.component';
import { CommentFormModalComponent } from './components/modal/comment-form/comment-form-modal.component';
import { OperationDetailsModalComponent } from './components/modal/operation-details/operation-details-modal.component';
import { IssueMaterialModalComponent } from './components/modal/issue-material/issue-material-modal.component';
import { DeadlineDetailsModalComponent } from './components/modal/deadline-details/deadline-details-modal.component';
import { MachineReportModalComponent } from './components/modal/machine-report/machine-report-modal.component';
import { EmployeeReportModalComponent } from './components/modal/employee-report/employee-report-modal.component';
import { RequestListModalComponent } from './components/modal/request-list/request-list-modal.component';
import { AddMaterialComponent } from './components/modal/add-material/add-material.component';
import { OfferRejectionModalComponent } from './components/modal/offer-rejection/offer-rejection-modal.component';
import { ChangeDeliveryDeadlineComponent } from './components/modal/change-delivery-deadline/change-delivery-deadline.component';
import { SuccessiveDeliveryComponent } from './components/modal/successive-delivery/successive-delivery.component';
import { DocumentAdditionalInfoComponent } from './components/modal/document-additional-info/document-additional-info.component';
import { OperationDisplacementModalComponent } from './components/modal/operation-displacement/operation-displacement.component';
import { AdditionalWorkRequestInfoComponent } from './components/modal/additional-work-request-info/additional-work-request-info.component';
import { TimeReportModalComponent } from './components/modal/time-report/time-report.component';
import { CorrectionIssueMaterialModal } from './components/modal/correction-issue-material/correction-issue-material-modal.component';
import { MaterialMinimumQuantityModalComponent } from './components/modal/material-minimum-quantity/material-minimum-quantity.component';
import { AdditionalProductComponent } from './components/modal/additional-product/additional-product.component';
import { AddUserToMailingListComponent } from './components/modal/add-user-mailing-list/add-user-mailing-list.component';
import { OfferMailUploadFileModalComponent } from './components/modal/offers-mail-upload-file/offers-mail-upload-file.component';
import { EmployeeOvertimeReportModalComponent } from './components/modal/employee-overtime-report/employee-overtime-report-modal.component';
import { AdditonalWarningInfoComponent } from './components/modal/additional-warning-info/additional-warning-info.component';
import { InfoModalComponent } from './components/modal/info/info-modal.component';
import { WarehouseOrderMaterialsListComponent } from './components/modal/warehouse-order-materials-list/warehouse-order-materials-list.component';
import { PackingDetailsModalComponent } from './components/modal/packing-details/packing-details.component';
import { TransporterChoiceModalComponent } from './components/modal/transporter-choice/transporter-choice-modal.component';
import { AddShippingMethodComponent } from './components/modal/add-shipping-method/add-shipping-method-component';
import { WorkOrderListModalComponent } from './components/modal/work-order-list/work-order-list-modal.component';
import { AddDevelopmentOperationModalComponent } from './components/modal/add-development-operation/add-development-operation.component';
import { AddDevelopmentWorksheetModalComponent } from './components/modal/add-development-worksheet/add-development-worksheet.component';
import { LanguageSelectorModalComponent } from './components/modal/language-selector/language-selector.component';
import { ImportFileModalComponent } from './components/modal/import-file/import-file-modal.component';
// import { OperationsDisplacementModalComponent } from './components/modal/operations-displacement/operations-displacement.component';

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
    NgxTooltipDirectivesModule,
    DatePickerModule,
  ],
  declarations: [
    // pipes
    TimeAgoPipe,
    SearchPipe,
    KeysPipe,
    DigitFormatPipe,
    // directives
    ClickElsewhereDirective,
    DisableControlDirective,
    ShowPasswordDirective,
    StopPropagationDirective,
    BackButtonDirective,
    DisableButtonDuringRequestDirective,
    // other shared components
    NotFoundComponent,
    HeaderComponent,
    UploadComponent,
    QuestionModalComponent,
    CommentModalComponent,
    CommentFormModalComponent,
    OperationDetailsModalComponent,
    EmptyStateComponent,
    SearchComponent,
    SearchComponent,
    EmptyStateComponent,
    SmallScreenComponent,
    SearchComponent,
    PaginationComponent,
    EmailComponent,
    MaterialComponent,
    TagComponent,
    OperationComponent,
    ProductionPlanHeaderComponent,
    OperationItemComponent,
    OrderItemComponent,
    EmployeeAvailabilityComponent,
    DeleteComponent,
    SectorTagComponent,
    MachineAvailabilityComponent,
    ProductSegmentComponent,
    SortComponent,
    CalendarComponent,
    ActivityLogComponent,
    // dropdown components
    DropDownComponent,
    OperationDropdownComponent,
    MaterialDropdownComponent,
    OrderDropdownComponent,
    MachineDropdownComponent,
    ObjectDropdownComponent,
    // progress bar components
    ProgressCircleComponent,
    ProgressBarComponent,
    ProgressPercentComponent,
    // autocomplete components
    AutocompleteComponent,
    ClientAutocompleteComponent,
    MaterialAutocompleteComponent,
    OrderAutocompleteComponent,
    ProductionOrderAutocompleteComponent,
    ActiveOperationsAutocompleteComponent,
    RequestAutocompleteComponent,
    ProductAutocompleteComponent,
    OperationAutocompleteComponent,
    ClientIncomeEstimationAutocompleteComponent,
    TransporterAutocompleteComponent,
    // modal components
    AlternativeMaterialComponent,
    IssueMaterialModalComponent,
    DeadlineDetailsModalComponent,
    MachineReportModalComponent,
    EmployeeReportModalComponent,
    EmployeeOvertimeReportModalComponent,
    RequestListModalComponent,
    AddMaterialComponent,
    OfferRejectionModalComponent,
    ChangeDeliveryDeadlineComponent,
    SuccessiveDeliveryComponent,
    DocumentAdditionalInfoComponent,
    OperationDisplacementModalComponent,
    // OperationsDisplacementModalComponent
    AdditionalWorkRequestInfoComponent,
    TimeReportModalComponent,
    CorrectionIssueMaterialModal,
    MaterialMinimumQuantityModalComponent,
    AdditionalProductComponent,
    AddUserToMailingListComponent,
    OfferMailUploadFileModalComponent,
    AdditonalWarningInfoComponent,
    InfoModalComponent,
    WarehouseOrderMaterialsListComponent,
    PackingDetailsModalComponent,
    TransporterChoiceModalComponent,
    AddShippingMethodComponent,
    WorkOrderListModalComponent,
    AddDevelopmentOperationModalComponent,
    AddDevelopmentWorksheetModalComponent,
    LanguageSelectorModalComponent,
    ImportFileModalComponent
  ],
  exports: [
    // pipes
    TimeAgoPipe,
    SearchPipe,
    KeysPipe,
    DigitFormatPipe,
    // directives
    ClickElsewhereDirective,
    DisableControlDirective,
    ShowPasswordDirective,
    StopPropagationDirective,
    BackButtonDirective,
    DisableButtonDuringRequestDirective,
    // other shared components
    NotFoundComponent,
    HeaderComponent,
    UploadComponent,
    QuestionModalComponent,
    CommentModalComponent,
    CommentFormModalComponent,
    OperationDetailsModalComponent,
    EmptyStateComponent,
    SearchComponent,
    SearchComponent,
    EmptyStateComponent,
    SmallScreenComponent,
    SearchComponent,
    PaginationComponent,
    EmailComponent,
    MaterialComponent,
    TagComponent,
    OperationComponent,
    ProductionPlanHeaderComponent,
    OperationItemComponent,
    OrderItemComponent,
    EmployeeAvailabilityComponent,
    DeleteComponent,
    SectorTagComponent,
    MachineAvailabilityComponent,
    ProductSegmentComponent,
    SortComponent,
    CalendarComponent,
    ActivityLogComponent,
    // dropdown components
    DropDownComponent,
    OperationDropdownComponent,
    MaterialDropdownComponent,
    OrderDropdownComponent,
    MachineDropdownComponent,
    ObjectDropdownComponent,
    // progress bar components
    ProgressCircleComponent,
    ProgressBarComponent,
    ProgressPercentComponent,
    // autocomplete components
    AutocompleteComponent,
    ClientAutocompleteComponent,
    MaterialAutocompleteComponent,
    OrderAutocompleteComponent,
    ProductionOrderAutocompleteComponent,
    ActiveOperationsAutocompleteComponent,
    RequestAutocompleteComponent,
    ProductAutocompleteComponent,
    OperationAutocompleteComponent,
    ClientIncomeEstimationAutocompleteComponent,
    TransporterAutocompleteComponent,
    // modal components
    AlternativeMaterialComponent,
    IssueMaterialModalComponent,
    DeadlineDetailsModalComponent,
    MachineReportModalComponent,
    EmployeeReportModalComponent,
    EmployeeOvertimeReportModalComponent,
    RequestListModalComponent,
    AddMaterialComponent,
    OfferRejectionModalComponent,
    ChangeDeliveryDeadlineComponent,
    SuccessiveDeliveryComponent,
    DocumentAdditionalInfoComponent,
    OperationDisplacementModalComponent,
    // OperationsDisplacementModalComponent
    AdditionalWorkRequestInfoComponent,
    TimeReportModalComponent,
    CorrectionIssueMaterialModal,
    MaterialMinimumQuantityModalComponent,
    AdditionalProductComponent,
    AddUserToMailingListComponent,
    OfferMailUploadFileModalComponent,
    AdditonalWarningInfoComponent,
    InfoModalComponent,
    WarehouseOrderMaterialsListComponent,
    PackingDetailsModalComponent,
    TransporterChoiceModalComponent,
    AddShippingMethodComponent,
    WorkOrderListModalComponent,
    AddDevelopmentOperationModalComponent,
    AddDevelopmentWorksheetModalComponent,
    LanguageSelectorModalComponent,
    ImportFileModalComponent
  ],
  providers: [
    UserService,
    MessageService,
    ProductionPlanService,
    RequestService,
    RoleHelper,
    DateHelper,
    SectorHelper,
    PermissionHelper,
    ShiftHelper,
    ConstantsHelper
  ],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [SharedService, provideNgxMask()]
    };
  }
}
